import { ReactElement, useEffect } from 'react';

import { useMachine } from '@xstate/react';
import { AnimatePresence, m as motion } from 'framer-motion';
import { navigationMachine } from 'machines/navigationMachine';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import { ErrorBoundary } from 'react-error-boundary';

import Spinner from 'chime-ui/components/Spinner';

import { PayAnyoneClaimInfo } from 'components/SubPages/ClaimInfo';
import { PayAnyoneConfirmation } from 'components/SubPages/Confirmation';
import {
  ErrorClaimed,
  ErrorContextualXState,
  ErrorExpired,
  ErrorGeneric,
} from 'components/SubPages/Error';
import MaintenanceMessage from 'components/SubPages/Error/MaintenanceMessage';
import {
  PayAnyoneLanding,
  ReturnUserLanding,
} from 'components/SubPages/Landing';

import { MachineContext } from 'hooks/useMachineContext';
import { useUserContext } from 'hooks/useUserContext';

import { trackPayAnyoneReceiverError } from 'utils/segment/trackingEvents';

const PF_TOKEN_COOKIE_NAME = 'pf_token';
const PF_PUBLIC_ID_COOKIE_NAME = 'pf_publicId';

type TokenProps = {
  data?: any;
  isError: boolean;
};

function PayAnyoneToken({ data, isError }: TokenProps): ReactElement {
  const [userData, setUserContext] = useUserContext();
  const [state, send, service] = useMachine(navigationMachine);
  const isMaintenanceModeEnabled = data.maintenanceMode;

  // We preset this variable so that we can listen to its change to set the context.
  // This component should be restructured as part of https://chime.atlassian.net/browse/PFG-853
  let senderInfo = null;

  useEffect(() => {
    if (data.previousClaim) {
      send('RETURN_USER_LANDING');
    } else {
      send('NEXT');
    }
  }, [data?.previousClaim, send]);

  const {
    query: { token },
  } = useRouter();

  const [, setCookie] = useCookies([
    PF_TOKEN_COOKIE_NAME,
    PF_PUBLIC_ID_COOKIE_NAME,
  ]);

  useEffect(() => {
    if (senderInfo && !userData) {
      setUserContext(senderInfo);
    }
  }, [senderInfo, setUserContext, userData]);

  const ErrorFallback = ({ publicId = data.publicId }) => {
    trackPayAnyoneReceiverError(
      'Landing Page',
      'Something went wrong',
      publicId
    );
    return <ErrorGeneric />;
  };

  // Just so we keep track of the state
  if (process.env.NODE_ENV === 'development') {
    console.log(state); // eslint-disable-line no-console
  }

  if (isMaintenanceModeEnabled) {
    return <MaintenanceMessage />;
  }

  if (isError) {
    return <ErrorFallback publicId={null} />;
  }

  if (data.status === 'succeeded') {
    trackPayAnyoneReceiverError(
      'Landing Page',
      'Payment claimed',
      data.publicId
    );
    return <ErrorClaimed />;
  }

  if (
    data.status === 'expired' ||
    data.status === 'cancelled' ||
    data.status === 'reversed'
  ) {
    trackPayAnyoneReceiverError(
      'Landing Page',
      'Transfer expired',
      data.publicId
    );
    return <ErrorExpired referralToken={data.sender.referralToken} />;
  }

  if (data.status === 'pending') {
    if (token !== state.context.token) {
      /**
       * We set a cookie here so consumer knows it needs to claim the PF
       * https://github.com/1debit/server-consumer/blob/3f03a8eae5453fc736ff5a53879db2686806c304/app/controllers/transactions_controller.rb#L968
       */
      setCookie(PF_TOKEN_COOKIE_NAME, token, {
        path: '/',
      });

      /**
       * We set the publicId cookie so we can track the user in enrollment
       */
      setCookie(PF_PUBLIC_ID_COOKIE_NAME, data.publicId, { path: '/' });

      send('TOKEN', { token, publicId: data.publicId });
    }

    senderInfo = {
      name: data.sender.name,
      amount: data.amount,
      memo: data.memo,
      bank: data.sender.bank,
      referralToken: data.sender.referralToken,
      publicId: data.publicId,
      senderId: data.sender.id,
      receiverIdentifier: data.receiverIdentifier,
      memoGifId: data.memoGifId,
      previousClaim: data.previousClaim,
      claimWithChime: data.claimWithChime,
      flipperFlags: data.flipperFlags,
      maintenanceMode: data.maintenanceMode,
    };

    // Checks to see if Receiver Identifier is a phone number
    if (
      senderInfo.receiverIdentifier.length === 10 &&
      senderInfo.receiverIdentifier.indexOf('@') === -1
    ) {
      setCookie('progressive_phone', senderInfo.receiverIdentifier, {
        path: '/',
      });
    }

    const handleError = (error: Error, info) => {
      window.Rollbar?.error(error.message, error, info);
    };

    return (
      <ErrorBoundary
        fallback={<ErrorFallback publicId={data.public} />}
        onError={handleError}
      >
        <MachineContext.Provider value={service}>
          {state.matches('landing') ? (
            <PageFadeInAnimation>
              <PayAnyoneLanding />
            </PageFadeInAnimation>
          ) : state.matches('returnUserLanding') ? (
            <PageFadeInAnimation>
              <ReturnUserLanding />
            </PageFadeInAnimation>
          ) : state.matches('claimInfo') ? (
            <PageFadeInAnimation>
              <PayAnyoneClaimInfo />
            </PageFadeInAnimation>
          ) : state.matches('confirmation') ? (
            <PageFadeInAnimation>
              <PayAnyoneConfirmation />
            </PageFadeInAnimation>
          ) : state.matches('error') ? (
            <PageFadeInAnimation>
              <ErrorContextualXState {...senderInfo} />
            </PageFadeInAnimation>
          ) : state.matches('loading') ? (
            <Spinner />
          ) : (
            <PageFadeInAnimation>
              <ErrorGeneric />
            </PageFadeInAnimation>
          )}
        </MachineContext.Provider>
      </ErrorBoundary>
    );
  }

  return <ErrorFallback />;
}

const PageFadeInAnimation = ({ children }) => (
  <AnimatePresence>
    <motion.div
      key={'confirmation'}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ type: 'easeIn' }}
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

export default PayAnyoneToken;
